import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePromotions } from '../hooks/use-promotions';
import { useDeletePromotion } from '../hooks/use-delete-promotion';
import { SearchBar } from '@/components/molecules/search-bar';
import { EmptyState } from '@/components/organisms/empty-state';
import { DeleteConfirmDialog } from '@/components/molecules/delete-confirm-dialog';
import { Skeleton } from '@/components/atoms/skeleton';
import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/molecules/tabs';
import { ResponsiveDataList, ColumnDef } from '@/components/organisms/responsive-data-list';
import { useTranslation } from 'react-i18next';
import { Plus, Tag, Percent, Truck, Gift, Package } from 'lucide-react';
import { formatDate, formatPrice, convertFromEur } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { toast } from 'sonner';
import type { PromotionStatus, PromotionType, Promotion } from '../types/promotions.types';

const PROMOTION_STATUSES = ['all', 'active', 'scheduled', 'expired', 'draft'] as const;

const statusVariantMap: Record<PromotionStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  active: 'default',
  paused: 'secondary',
  cancelled: 'destructive',
  scheduled: 'secondary',
  expired: 'destructive',
  draft: 'outline',
};

const typeIconMap: Record<PromotionType, React.ElementType> = {
  percentage: Percent,
  fixed_amount: Tag,
  free_shipping: Truck,
  buy_x_get_y: Gift,
  bundle: Gift,
};

export function PromotionListContainer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState<string>('all');

  const { data: promotionsData, isLoading, error } = usePromotions({
    search,
    status: status === 'all' ? undefined : status as PromotionStatus,
  });
  const deletePromotion = useDeletePromotion();
  const promotions = promotionsData?.promotions ?? [];
  const { currency } = useConfigStore();

  const handleRowClick = (promotion: Promotion) => {
    navigate(`/promotions/${promotion.refId}`);
  };

  const handleDelete = (promotion: Promotion) => {
    deletePromotion.mutate(promotion.refId, {
      onSuccess: () => {
        toast.success(t('promotions.deleteSuccess', 'Promotion supprimée avec succès'));
      },
      onError: () => {
        toast.error(t('promotions.deleteError', 'Erreur lors de la suppression de la promotion'));
      },
    });
  };

  // Format promotion value for display
  const formatPromotionValue = (promotion: Promotion) => {
    if (promotion.type === 'percentage') {
      return `${promotion.value}%`;
    }
    if (promotion.type === 'free_shipping') {
      return t('promotions.freeShipping');
    }
    if (promotion.type === 'buy_x_get_y') {
      return `${promotion.buyQuantity || 0}+${promotion.getQuantity || 0}`;
    }
    return formatPrice(convertFromEur(promotion.value, currency), currency);
  };

  // Define table columns for desktop view
  const columns: ColumnDef<Promotion>[] = React.useMemo(
    () => [
      {
        id: 'type',
        header: '',
        cell: (promotion) => {
          const TypeIcon = typeIconMap[promotion.type] || Tag;
          return (
            <div
              className="rounded-md p-2 w-fit"
              style={{
                backgroundColor: promotion.badgeColor ? `${promotion.badgeColor}20` : 'hsl(var(--muted))',
              }}
            >
              <TypeIcon
                className="size-4"
                style={{ color: promotion.badgeColor || undefined }}
              />
            </div>
          );
        },
        className: 'w-16',
      },
      {
        id: 'name',
        header: t('promotions.name'),
        cell: (promotion) => (
          <div>
            <p className="font-medium">{promotion.displayName || promotion.name}</p>
            {promotion.displayName && promotion.displayName !== promotion.name && (
              <p className="text-xs text-muted-foreground">{promotion.name}</p>
            )}
          </div>
        ),
        sortable: true,
      },
      {
        id: 'value',
        header: t('promotions.value'),
        cell: (promotion) => (
          <span
            className="font-bold"
            style={{ color: promotion.badgeColor || undefined }}
          >
            {formatPromotionValue(promotion)}
          </span>
        ),
        sortable: true,
      },
      {
        id: 'usage',
        header: t('promotions.usage'),
        cell: (promotion) => (
          <span className="text-muted-foreground">
            {promotion.currentUsage}
            {promotion.maxUsage ? ` / ${promotion.maxUsage}` : ` (${t('promotions.unlimited')})`}
          </span>
        ),
      },
      {
        id: 'products',
        header: t('promotions.products'),
        cell: (promotion) => (
          promotion.productsCount !== undefined && promotion.productsCount > 0 ? (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Package className="size-3" />
              {promotion.productsCount}
            </span>
          ) : (
            <span className="text-muted-foreground">-</span>
          )
        ),
      },
      {
        id: 'period',
        header: t('promotions.period'),
        cell: (promotion) => (
          <span className="text-sm text-muted-foreground">
            {formatDate(promotion.startDate, 'dd/MM')} - {formatDate(promotion.endDate, 'dd/MM/yy')}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('common.status'),
        cell: (promotion) => (
          <Badge variant={statusVariantMap[promotion.status]}>
            {t(`promotions.status.${promotion.status}`)}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (promotion) => (
          <DeleteConfirmDialog
            itemName={promotion.displayName || promotion.name}
            onConfirm={() => handleDelete(promotion)}
            isLoading={deletePromotion.isPending}
          />
        ),
        className: 'w-12',
      },
    ],
    [t, currency, deletePromotion.isPending, handleDelete]
  );

  // Card renderer for mobile view (keeping existing design)
  const renderCard = (promotion: Promotion, index: number) => {
    const TypeIcon = typeIconMap[promotion.type] || Tag;
    return (
      <Card
        key={promotion.refId || `promotion-${index}`}
        className="cursor-pointer hover:border-primary/50 transition-colors"
        style={{ borderLeftColor: promotion.badgeColor, borderLeftWidth: promotion.badgeColor ? 4 : undefined }}
        onClick={() => handleRowClick(promotion)}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <div
              className="rounded-md p-2"
              style={{
                backgroundColor: promotion.badgeColor ? `${promotion.badgeColor}20` : undefined,
              }}
            >
              <TypeIcon
                className="size-4"
                style={{ color: promotion.badgeColor || undefined }}
              />
            </div>
            <div>
              <CardTitle className="text-base font-medium">
                {promotion.displayName || promotion.name}
              </CardTitle>
              {promotion.displayName && promotion.displayName !== promotion.name && (
                <p className="text-xs text-muted-foreground">{promotion.name}</p>
              )}
            </div>
          </div>
          <Badge variant={statusVariantMap[promotion.status]}>
            {t(`promotions.status.${promotion.status}`)}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('promotions.value')}</span>
            <span className="font-bold text-lg" style={{ color: promotion.badgeColor || undefined }}>
              {formatPromotionValue(promotion)}
            </span>
          </div>
          {promotion.minPurchaseAmount && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t('promotions.minPurchase')}</span>
              <span>{formatPrice(convertFromEur(promotion.minPurchaseAmount, currency), currency)}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('promotions.usage')}</span>
            <span>
              {promotion.currentUsage}
              {promotion.maxUsage ? ` / ${promotion.maxUsage}` : ` (${t('promotions.unlimited')})`}
            </span>
          </div>
          {promotion.productsCount !== undefined && promotion.productsCount > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t('promotions.products')}</span>
              <span className="flex items-center gap-1">
                <Package className="size-3" />
                {promotion.productsCount}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('promotions.period')}</span>
            <span className="text-xs">
              {formatDate(promotion.startDate, 'dd/MM')} - {formatDate(promotion.endDate, 'dd/MM/yyyy')}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Card skeleton for loading state
  const renderCardSkeleton = () => (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-3/4" />
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-1/3" />
        </div>
      </CardContent>
    </Card>
  );

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('promotions.error')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <SearchBar
          onSearch={setSearch}
          placeholder={t('promotions.searchPlaceholder')}
          className="sm:max-w-xs"
        />
        <div className="flex items-center gap-4 flex-wrap">
          <Tabs value={status} onValueChange={setStatus}>
            <TabsList className="flex-wrap">
              {PROMOTION_STATUSES.map((s) => (
                <TabsTrigger key={s} value={s}>
                  {t(`promotions.status.${s}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Link to="/promotions/new">
            <Button>
              <Plus className="size-4 mr-2" />
              {t('promotions.addPromotion')}
            </Button>
          </Link>
        </div>
      </div>

      <ResponsiveDataList
        data={promotions}
        columns={columns}
        renderCard={renderCard}
        renderCardSkeleton={renderCardSkeleton}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        keyExtractor={(promotion, index) => promotion.refId || `promotion-${index}`}
        cardGridClassName="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
        emptyState={
          <EmptyState
            title={t('promotions.emptyTitle')}
            description={t('promotions.emptyDescription')}
            action={
              <Link to="/promotions/new">
                <Button>
                  <Plus className="size-4 mr-2" />
                  {t('promotions.addPromotion')}
                </Button>
              </Link>
            }
          />
        }
      />
    </div>
  );
}
